// Turns an authenticated API file route into a URL that native <img>/<video>/
// <a>/<iframe> elements can load — from ANY client. The one loader for files
// the dashboard shows: media the agent shows (`/api/media`, MediaEmbed) and
// files attached to messages (`/api/chat/sessions/:id/attachments/:id`).
//
// Every /api route sits behind bearer-token auth for non-loopback clients
// (relay, phone PWA), but native media elements cannot attach an
// Authorization header — an API URL used raw as an `<img>` source works on the
// loopback desktop and fails everywhere else (MA-054, MA-451).
//
// Strategy:
//   • No stored token (loopback desktop) → the direct URL. No extra fetch.
//   • Token present (relay / PWA) → fetch the bytes with apiFetch (which
//     attaches the Bearer header) and hand back a blob object URL. Blob URLs
//     work in <img>, <video>, <audio>, <iframe> (PDF), and as download hrefs.
//     Revoked on unmount / path change so they don't leak.
import { useEffect, useState } from 'react';
import { BASE_URL, apiFetch, getApiToken } from './api';

/**
 * API path for a file the agent shows, by its path on the desktop. The server
 * only serves files under ~/.onestone/ or the OS temp folder (chat-upload.ts).
 */
export function mediaApiPath(path: string): string {
  return `/api/media?path=${encodeURIComponent(path)}`;
}

/** API path for a file attached to one of a conversation's messages. */
export function attachmentApiPath(conversationId: string, attachmentId: string): string {
  return `/api/chat/sessions/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}`;
}

export interface FileUrlState {
  /** Loadable URL (direct or blob:), or null while loading, on error, or with no path. */
  url: string | null;
  /** Human-readable failure, or null. Loading = url null + error null. */
  error: string | null;
}

/** A loadable URL for `apiPath` (relative, no BASE_URL); `null` loads nothing. */
export function useApiFileUrl(apiPath: string | null): FileUrlState {
  // Token presence decides the strategy. Read once per render — the token only
  // changes on pairing/unpairing, which reloads the app.
  const needsAuth = getApiToken().length > 0;
  const directUrl = apiPath === null ? null : `${BASE_URL}${apiPath}`;
  const [state, setState] = useState<FileUrlState>(() =>
    needsAuth ? { url: null, error: null } : { url: directUrl, error: null },
  );

  useEffect(() => {
    if (apiPath === null || !needsAuth) {
      setState({ url: directUrl, error: null });
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setState({ url: null, error: null });
    apiFetch(apiPath)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}${res.status === 401 ? ' — auth rejected' : ''}`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ url: objectUrl, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({ url: null, error: err instanceof Error ? err.message : String(err) });
        }
      });
    return () => {
      cancelled = true;
      // Revoke on unmount/path change. The lightbox opens while the owning
      // message is mounted, so the blob stays valid for the preview lifetime.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [apiPath, needsAuth, directUrl]);

  return state;
}
